import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '../icons/Icon';
import * as vfsClient from '../../utils/vfsClient';
import { formatRelativeTime } from '../../utils/relativeTime';
import VendorName from '../sandbox/VendorName';
import {
  actionLabel, actorLabel, overrideReason, aiActionCount, clientLabel, isOrgKey, keySourceLabel, keySourceShort, keySplit, modelLabel,
} from './aiProvenanceLabels';
import './AiProvenanceCard.css';

const TIMELINE_LIMIT = 100;
const formatNumber = (value) => new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value || 0);
const when = (value) => {
  if (!value) { return ''; }
  const relative = formatRelativeTime(value);
  return relative === 'now' ? 'Just now' : relative;
};
const fullDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

// One row of the timeline: when, what, who, via which tool, model, key, how many paths, playbook.
function TimelineRow({ event, me }) {
  const ai = event.ai || null;
  const model = ai ? modelLabel(ai) : '';
  const paths = event.target?.paths?.count || 0;
  const deploy = event.target?.deploy;
  return (
    <li className="aip-row" data-testid="ai-provenance-event">
      <time dateTime={event.ts} title={fullDate(event.ts)}>{when(event.until || event.ts)}</time>
      <div className="aip-row-main">
        <div className="aip-row-line">
          <b>{actionLabel(event.action)}</b>
          {event.count > 1 && <span className="aip-muted">×{event.count}</span>}
          <span className="aip-muted">by</span> <span>{actorLabel(event.actor, me)}</span>
          {clientLabel(event.client) && <><span className="aip-muted">via</span> <span><VendorName name={clientLabel(event.client)} /></span></>}
        </div>
        <div className="aip-row-meta">
          {model && <span className="aip-tag"><VendorName name={ai.provider || ai.model} size={12}>{model}</VendorName></span>}
          {ai?.keySource && <span className={`aip-tag aip-key-${isOrgKey(ai.keySource) ? 'org' : ai.keySource}`} title={keySourceLabel(ai.keySource)}>{keySourceShort(ai.keySource)}</span>}
          {ai?.playbook?.name && <span className="aip-tag" title="Playbook">{ai.playbook.name}</span>}
          {ai?.agent && <span className="aip-tag" title="Agent">{ai.agent}</span>}
          {paths > 0 && <span className="aip-muted">{paths} {paths === 1 ? 'path' : 'paths'}</span>}
          {deploy?.kind && <span className="aip-muted">{deploy.kind}{deploy.system ? ` · ${deploy.system}` : ''}{deploy.version ? ` v${deploy.version}` : ''}</span>}
          {overrideReason(event) && <span className="aip-muted" data-testid="ai-provenance-reason">{overrideReason(event)}</span>}
          {event.derived && <span className="aip-muted" title="Estimated from the project history">estimated</span>}
        </div>
      </div>
    </li>
  );
}

TimelineRow.propTypes = { event: PropTypes.object.isRequired, me: PropTypes.string };

/**
 * "AI used in this project": which AI tools, models, keys and playbooks touched the project (from vfs-service's
 * provenance), a shadow-AI warning when something is outside the organization's approved list, and, for owners,
 * editors and admins, the timeline of AI actions. Only metadata: no prompts, messages or file contents.
 */
export default function AiProvenanceCard({ projectId, currentUserSub }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [toolFilter, setToolFilter] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!projectId) { return undefined; }
    let live = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await vfsClient.getProvenance(projectId, {
          limit: TIMELINE_LIMIT,
          action: actionFilter || undefined,
          tool: toolFilter || undefined,
        });
        if (live) { setData(result || {}); }
      } catch (err) {
        if (live) { setError(err?.message || 'The AI history could not be loaded.'); }
      } finally {
        if (live) { setLoading(false); }
      }
    })();
    return () => { live = false; };
  }, [projectId, actionFilter, toolFilter, reload]);

  const summary = data?.summary || null;
  const shadow = data?.shadow || null;
  const canSeeTimeline = data ? data.canSeeTimeline !== false : false;

  // The server filters; filtering again here keeps the list right even if it ignores a filter.
  const timeline = useMemo(() => (data?.timeline || []).filter((event) => (
    (!actionFilter || event.action === actionFilter) && (!toolFilter || event.client?.kind === toolFilter)
  )), [data, actionFilter, toolFilter]);

  const actionOptions = useMemo(() => Object.entries(summary?.actions || {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([action]) => action), [summary]);
  const toolOptions = useMemo(() => (summary?.tools || []).filter((tool) => tool.kind), [summary]);

  const header = (
    <div className="aip-head">
      <h3><Icon name="sparkles" size={16} glow={false} />AI used in this project</h3>
      {shadow?.flagged && <span className="aip-shadow-badge" role="status">Shadow AI</span>}
    </div>
  );

  if (!data && loading) {
    return (
      <div className="pd-card aip" data-testid="ai-provenance-card">
        {header}
        <div className="pd-empty">Loading AI history…</div>
      </div>
    );
  }

  if (!data && error) {
    return (
      <div className="pd-card aip" data-testid="ai-provenance-card">
        {header}
        <div className="pd-empty aip-error" role="alert">
          The AI history could not be loaded.
          <button className="pd-link aip-retry" onClick={() => setReload((value) => value + 1)} type="button">Try again</button>
        </div>
      </div>
    );
  }

  const filtering = Boolean(actionFilter || toolFilter);
  if (!summary || (!summary.events && !filtering)) {
    return (
      <div className="pd-card aip" data-testid="ai-provenance-card">
        {header}
        <div className="pd-empty">No AI was recorded in this project yet. When an assistant, an AI coding tool or a playbook changes it, it shows up here.</div>
      </div>
    );
  }

  const split = keySplit(summary.keySources);
  const tokens = summary.tokens && (summary.tokens.in || summary.tokens.out) ? summary.tokens : null;
  const playbooks = summary.playbooks || [];
  const agents = summary.agents || [];
  const models = summary.models || [];
  const tools = summary.tools || [];

  return (
    <div className="pd-card aip" data-testid="ai-provenance-card">
      {header}
      <p className="pd-why">
        Which AI tools, models and keys changed this project. Only metadata is kept: never prompts, messages or file contents.
      </p>

      {shadow?.flagged && (
        <div className="aip-shadow" data-testid="ai-provenance-shadow">
          <Icon name="warning" size={16} glow={false} />
          <div>
            <b>Shadow AI</b>
            {shadow.explanation && <p>{shadow.explanation}</p>}
            {shadow.reasons?.length > 0 && (
              <ul>
                {shadow.reasons.map((reason) => (
                  <li key={`${reason.kind}:${reason.value}`}>
                    {reason.label || reason.value}
                    <span className="aip-muted"> · {reason.kind}{reason.count ? ` · ${reason.count}×` : ''}{reason.lastAt ? ` · ${when(reason.lastAt)}` : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {summary.derived && (
        <p className="aip-note" data-testid="ai-provenance-derived">
          <Icon name="info" size={14} glow={false} />
          Estimated from the project history: it was changed before AI tracking started.
        </p>
      )}

      <div className="aip-stats">
        <div><span>{aiActionCount(summary)}</span><small>AI actions</small></div>
        <div><span>{tools.length}</span><small>{tools.length === 1 ? 'tool' : 'tools'}</small></div>
        <div><span>{models.length}</span><small>{models.length === 1 ? 'model' : 'models'}</small></div>
        {tokens && <div><span>{formatNumber((tokens.in || 0) + (tokens.out || 0))}</span><small title={`${tokens.in || 0} in · ${tokens.out || 0} out`}>tokens</small></div>}
      </div>

      {split.total > 0 && (
        <div className="aip-split" aria-label="Keys used">
          <div className="aip-split-bar">
            {split.org > 0 && <span className="aip-key-org" style={{ flexGrow: split.org }} />}
            {split.own > 0 && <span className="aip-key-own-key" style={{ flexGrow: split.own }} />}
            {split.tool > 0 && <span className="aip-key-tool-own" style={{ flexGrow: split.tool }} />}
          </div>
          <div className="aip-split-legend">
            <span><i className="aip-key-org" />Organization key {split.org}</span>
            <span><i className="aip-key-own-key" />Own key {split.own}</span>
            {split.tool > 0 && <span><i className="aip-key-tool-own" />Tool&apos;s own model {split.tool}</span>}
          </div>
        </div>
      )}

      {tools.length > 0 && (
        <>
          <div className="pd-sub">Tools</div>
          <ul className="aip-list">
            {tools.map((tool) => (
              <li key={tool.kind || tool.label}>
                <span className="aip-name"><VendorName name={tool.label || tool.kind} /></span>
                <span className="aip-muted">{tool.count}× · {when(tool.lastAt)}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {models.length > 0 && (
        <>
          <div className="pd-sub">Models</div>
          <ul className="aip-list">
            {models.map((model) => (
              <li key={`${model.provider}/${model.model}`}>
                <span className="aip-name"><VendorName name={model.provider || model.model}>{modelLabel(model)}</VendorName></span>
                <span className="aip-keys">
                  {(model.keySources || []).map((source) => (
                    <span className={`aip-tag aip-key-${isOrgKey(source) ? 'org' : source}`} key={source} title={keySourceLabel(source)}>{keySourceShort(source)}</span>
                  ))}
                  <span className="aip-muted">{model.count}×</span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {(playbooks.length > 0 || agents.length > 0) && (
        <>
          <div className="pd-sub">Playbooks and agents</div>
          <div className="aip-chips">
            {playbooks.map((playbook) => (
              <span className="pd-chip" key={`pb:${playbook.id || playbook.name}`} title={`Playbook · ${playbook.count}×`}>
                {playbook.name || playbook.id}{playbook.version ? ` v${playbook.version}` : ''}
              </span>
            ))}
            {agents.map((agent) => (
              <span className="pd-chip" key={`ag:${agent.name}`} title={`Agent · ${agent.count}×`}>{agent.name}</span>
            ))}
          </div>
        </>
      )}

      {canSeeTimeline ? (
        <>
          <div className="aip-timeline-head">
            <div className="pd-sub">Timeline</div>
            <div className="aip-filters">
              <label>
                <span>Action</span>
                <select onChange={(event) => setActionFilter(event.target.value)} value={actionFilter}>
                  <option value="">All actions</option>
                  {actionOptions.map((action) => <option key={action} value={action}>{actionLabel(action)}</option>)}
                </select>
              </label>
              <label>
                <span>Tool</span>
                <select onChange={(event) => setToolFilter(event.target.value)} value={toolFilter}>
                  <option value="">All tools</option>
                  {toolOptions.map((tool) => <option key={tool.kind} value={tool.kind}>{tool.label || tool.kind}</option>)}
                </select>
              </label>
            </div>
          </div>
          {error && <div className="pd-empty aip-error" role="alert">The AI history could not be loaded.</div>}
          {timeline.length > 0 ? (
            <ol className="aip-timeline" aria-busy={loading}>
              {timeline.map((event) => <TimelineRow event={event} me={currentUserSub} key={event.id || `${event.ts}:${event.action}`} />)}
            </ol>
          ) : !error && (
            <div className="pd-empty">{loading ? 'Loading…' : filtering ? 'No AI actions match these filters.' : 'No AI actions recorded yet.'}</div>
          )}
          {data?.retentionDays && <p className="aip-muted aip-retention">Kept for {data.retentionDays} days.</p>}
        </>
      ) : (
        <p className="aip-note" data-testid="ai-provenance-viewer-note">
          <Icon name="info" size={14} glow={false} />
          The per-person timeline is visible to owners, editors and admins.
        </p>
      )}
    </div>
  );
}

AiProvenanceCard.propTypes = {
  projectId: PropTypes.string,
  // The signed-in person's sub: their own actions read "You".
  currentUserSub: PropTypes.string,
};
